import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { clearUserCache } from '../lib/clearUserCache';
import { setGlobalUser, getCurrentUserId } from '../lib/getUserId';
import { analytics } from '../lib/analytics';
import {
  buildRuntimeLoginFailedProps,
  createLoginFailureError,
  LOGIN_ANALYTICS_SOURCE,
} from '../lib/analytics/loginObservability';
import { beginLogoutSessionCleanup, clearLogoutSessionCleanup } from '../lib/authSession';
import { API_BASE_URL } from '../lib/apiBaseUrl';
import type { AppleLoginPayload, LoginMethod } from '../lib/authTypes';

interface User {
  id: number;
  email: string;
  name: string;
  avatar_url?: string;
  created_at: string;
  last_login?: string;
  research_history_count?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: (googleToken: string) => Promise<void>;
  loginWithApple: (payload: AppleLoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const SIGNUP_FRESHNESS_MS = 10 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  const completeLogin = async (
    method: LoginMethod,
    endpoint: string,
    body: Record<string, unknown>
  ) => {
    try {
      // CRITICAL SECURITY: Get current user ID before login to detect user switch
      const previousUserId = getCurrentUserId();

      analytics.trackLoginStarted({
        method,
        source: LOGIN_ANALYTICS_SOURCE,
      });
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Login failed with error:', errorData);
        throw createLoginFailureError(errorData.detail || 'Login failed', {
          stage: 'backend',
          reasonCode: 'backend_rejected',
          statusCode: response.status,
        });
      }

      const data = await response.json();
      const newUserId = data.user?.id;
      clearLogoutSessionCleanup();
      
      // CRITICAL SECURITY: Clear ALL cache BEFORE setting new user data
      // This prevents the new user from seeing the previous user's cached data
      console.log(`[Auth] Login: previous user ${previousUserId}, new user ${newUserId}`);
      
      // Always clear cache on login to ensure clean state
      clearUserCache();
      
      // Clear any existing user state to prevent flash of old data
      setUser(null);
      setGlobalUser(null);
      
      // Store token in localStorage
      localStorage.setItem('access_token', data.access_token);
      
      // Now set the new user data - this triggers re-render with fresh user
      // Use setTimeout to ensure cache clear is complete before setting new user
      await new Promise(resolve => setTimeout(resolve, 50));
      
      setGlobalUser(data.user);
      setUser(data.user);
      
      // Track login with Amplitude - MUST identify user before tracking events!
      const createdAtMs = data.user?.created_at ? new Date(data.user.created_at).getTime() : Number.NaN;
      const isFreshAccount =
        Number.isFinite(createdAtMs) && Math.max(0, Date.now() - createdAtMs) <= SIGNUP_FRESHNESS_MS;
      const isNewUser = !previousUserId && isFreshAccount;
      const userId = data.user.id.toString();
      
      console.log(`[Auth] About to identify user in Amplitude: ID=${userId}, email=${data.user.email}`);
      
      // IMPORTANT: await identify to ensure user ID is set before tracking events
      try {
        await analytics.identify({
          id: userId,
          email: data.user.email,
          name: data.user.name,
          avatar_url: data.user.avatar_url,
          plan: 'free', // Will be updated when billing status is fetched
          created_at: data.user.created_at,
        });
      } catch (identifyError) {
        throw createLoginFailureError(
          identifyError instanceof Error ? identifyError.message : 'Analytics identify failed',
          {
            stage: 'identify',
            reasonCode: 'identify_failed',
          }
        );
      }
      
      console.log(`[Auth] Amplitude identify completed, now tracking login event`);
      
      if (isNewUser) {
        analytics.trackSignup({ method });
      } else {
        analytics.trackLogin({ method });
      }

      // Fire Google Ads conversion — counts both new signups and returning logins
      if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'conversion', {
          send_to: 'AW-18077622282',
          method,
          is_new_user: isNewUser,
        });
      }

      analytics.trackInitialSession();
      
      console.log(`[Auth] Login successful for user ${data.user?.email} (ID: ${newUserId})`);
    } catch (error) {
      console.error('Login error:', error);
      const loginFailedProps = buildRuntimeLoginFailedProps(method, error);
      analytics.trackLoginFailed(loginFailedProps);
      analytics.trackError({
        error_type: 'auth_failed',
        error_message: loginFailedProps.error_message || 'Login failed',
        page: 'login',
        method,
        stage: loginFailedProps.stage,
        reason_code: loginFailedProps.reason_code,
        ...(typeof loginFailedProps.status_code === 'number'
          ? { status_code: loginFailedProps.status_code }
          : {}),
      });
      if (typeof loginFailedProps.status_code === 'number') {
        analytics.trackAPIError({
          endpoint: endpoint,
          status_code: loginFailedProps.status_code,
          error_message: loginFailedProps.error_message || 'Login failed',
        });
      }
      throw error;
    }
  };

  const loginWithGoogle = async (googleToken: string) => {
    await completeLogin('google', '/auth/google', { google_token: googleToken });
  };

  const loginWithApple = async (payload: AppleLoginPayload) => {
    await completeLogin('apple', '/auth/apple', {
      identity_token: payload.identityToken,
      authorization_code: payload.authorizationCode,
      user: payload.user,
    });
  };

  const logout = async () => {
    console.log('[Auth] Logout initiated');
    beginLogoutSessionCleanup();
    const token = localStorage.getItem('access_token');

    // Track logout FIRST using dedicated method (bypasses isAuthenticated check)
    // This ensures the logout event is attributed to the correct user
    analytics.trackLogout();
    
    // Small delay to ensure the event is sent before resetting
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Now reset analytics (clears user identity and disables tracking)
    analytics.reset();

    // CRITICAL SECURITY: Clear ALL user data immediately
    // Order matters: clear cache FIRST, then user state
    clearUserCache();
    
    // Remove auth token
    localStorage.removeItem('access_token');
    
    // Clear user state
    setGlobalUser(null);
    setUser(null);
    
    console.log('[Auth] User logged out, all cache cleared');

    // Fire-and-forget API call so sessions are closed server-side
    if (!token) return;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeout);
    } catch (error) {
      console.error('Logout error (non-blocking):', error);
    }
  };

  const transientAuthRetryRef = useRef(false);

  // Restore the session from the JWT we already hold. Used when /auth/check
  // fails for reasons that say nothing about the token (timeouts, 5xx during
  // deploys, network/CORS errors) — a server problem is not a logout.
  const recoverSessionFromStoredToken = async (storedToken: string, context: string): Promise<boolean> => {
    try {
      const payload = JSON.parse(atob(storedToken.split('.')[1]));
      const minimalUser: User = {
        id: Number(payload.sub),
        email: payload.email || '',
        name: payload.email?.split('@')[0] || 'User',
        created_at: '',
      };
      setGlobalUser(minimalUser);
      setUser(minimalUser);

      if (minimalUser.email) {
        try {
          await analytics.identify({
            id: String(minimalUser.id),
            email: minimalUser.email,
            name: minimalUser.name,
            plan: 'free',
            created_at: minimalUser.created_at,
          });

          analytics.trackInitialSession();
        } catch (analyticsError) {
          console.warn(`[Auth] Analytics identify failed during ${context} recovery (non-blocking):`, analyticsError);
        }
      } else {
        console.warn(`[Auth] ${context} recovery skipped analytics identify because the token had no email`);
      }
      return true;
    } catch {
      console.error(`[Auth] Could not decode stored token after ${context}`);
      return false;
    }
  };

  const scheduleAuthRecheck = () => {
    if (transientAuthRetryRef.current) return;
    transientAuthRetryRef.current = true;
    window.setTimeout(() => {
      void checkAuthStatus();
    }, 30000);
  };

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        clearUserCache();
        setGlobalUser(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      const controller = new AbortController();
      const fetchTimeout = window.setTimeout(() => controller.abort(), 10000);
      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/auth/check`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(fetchTimeout);
      }

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.user) {
          clearLogoutSessionCleanup();
          // CRITICAL: Check if this is a different user than what's cached
          const cachedUserId = getCurrentUserId();
          if (cachedUserId && cachedUserId !== data.user.id) {
            console.log(`[Auth] User ID mismatch: cached ${cachedUserId}, auth ${data.user.id}. Clearing cache.`);
            clearUserCache();
          }
          
          setGlobalUser(data.user);
          setUser(data.user);
          
          // Identify returning user with Amplitude
          const userId = data.user.id.toString();
          console.log(`[Auth] checkAuthStatus: Identifying returning user in Amplitude: ID=${userId}, email=${data.user.email}`);
          
          // IMPORTANT: identify user but don't let analytics failures block auth
          try {
            await analytics.identify({
              id: userId,
              email: data.user.email,
              name: data.user.name,
              avatar_url: data.user.avatar_url,
              plan: 'free',
              created_at: data.user.created_at,
            });
            
            console.log(`[Auth] checkAuthStatus: Amplitude identify completed for returning user`);
            analytics.trackInitialSession();
          } catch (analyticsError) {
            console.warn('[Auth] Analytics identify failed (non-blocking):', analyticsError);
          }
        } else {
          // Not authenticated - clear everything
          console.log('[Auth] Auth check returned not authenticated, clearing data');
          clearUserCache();
          localStorage.removeItem('access_token');
          setGlobalUser(null);
          setUser(null);
          // No tracking for unauthenticated users
        }
      } else if (response.status === 401 || response.status === 403) {
        // The server actually rejected the token — authoritative logout.
        console.log('[Auth] Auth check rejected the token, clearing data');
        analytics.trackAPIError({
          endpoint: '/auth/check',
          status_code: response.status,
          error_message: `Auth check failed with status ${response.status}`,
        });
        clearUserCache();
        localStorage.removeItem('access_token');
        setGlobalUser(null);
        setUser(null);
      } else {
        // 5xx/429: Render serves 502/503 during deploys and cold starts.
        // Keep the session and re-check once in the background.
        console.warn(`[Auth] Auth check got transient ${response.status} — keeping session`);
        analytics.trackAPIError({
          endpoint: '/auth/check',
          status_code: response.status,
          error_message: `Auth check transient failure with status ${response.status}`,
        });
        const recovered = await recoverSessionFromStoredToken(token, `transient ${response.status}`);
        if (!recovered) {
          clearUserCache();
          localStorage.removeItem('access_token');
          setGlobalUser(null);
          setUser(null);
        } else {
          scheduleAuthRecheck();
        }
      }
    } catch (error) {
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        // Timeouts, flaky cellular, CORS-less 502 error pages, backgrounded-tab
        // fetch aborts — none of these say anything about the token's validity.
        console.warn(
          isTimeout
            ? '[Auth] Auth check timed out — recovering session from stored token'
            : `[Auth] Auth check network error — recovering session from stored token: ${error}`
        );
        const recovered = await recoverSessionFromStoredToken(storedToken, isTimeout ? 'timeout' : 'network error');
        if (!recovered) {
          clearUserCache();
          localStorage.removeItem('access_token');
          setGlobalUser(null);
          setUser(null);
        } else if (!isTimeout) {
          scheduleAuthRecheck();
        }
      } else {
        console.error('Auth check error:', error);
        clearUserCache();
        setGlobalUser(null);
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    loginWithGoogle,
    loginWithApple,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to get auth header for API calls
export const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}; 